/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useEffect, useState } from "react";
import { ChartPluginsProps, ChartPluginsStyledComponentProps } from "./types";
import styled from "styled-components/macro";
import VerticalTimeline from "./components/VerticalTimeline/VerticalTimeline";
import TotalsRow from "./components/VerticalTimeline/TotalsRow";
import {
  lightTextSubheading,
  lightSurface0,
  summarySectionBottomBackground,
} from "../utils/tier0-colors";
import { useMeasure } from "react-use";
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
  Theme,
} from "@mui/material/styles";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import {
  BREAKPOINT_DESKTOP_XL,
  COLUMN_PADDING_HZ_PCT,
  COLUMN_WIDTHS_PCT,
} from "../utils/tier0-utils";
import { defaultSupersetTheme } from "../utils/defaultSupersetTheme";
import Spinner from "components/SimpleComponents/Spinner/Spinner";

/**
 * ******************* WHAT YOU CAN BUILD HERE *******************
 *  In essence, a chart is given a few key ingredients to work with:
 *  * Data: provided via `props.data`
 *  * A DOM element
 *  * FormData (your controls!) provided as props by transformProps.ts
 */

const ThemeManager = ({
  theme,
  children,
}: {
  theme: Theme;
  children: React.JSX.Element;
}): React.JSX.Element => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>{children}</ThemeProvider>
  </StyledEngineProvider>
);

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#0BA6D6",
      contrastText: "#000",
    },
    secondary: {
      main: "#4177b7",
    },
    text: {
      primary: "#000",
      secondary: "#58595b",
    },
    background: {
      default: "#fff",
      paper: "#fff",
    },
  },
  // * leaving this for now to debug mui icon "cannot .create on undefined (theme.transitions.create)"
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      // most basic recommended timing
      standard: 300,
      // this is to be used in complex animations
      complex: 375,
      // recommended when something is entering screen
      enteringScreen: 225,
      // recommended when something is leaving screen
      leavingScreen: 195,
    },
  },
});

export default function MeritTier0HourlyTableChart(props: ChartPluginsProps) {
  return (
    <ThemeManager theme={lightTheme}>
      <MeritTier0HourlyTableChartUnconnected {...props} />
    </ThemeManager>
  );
}
function MeritTier0HourlyTableChartUnconnected(props: ChartPluginsProps) {
  // height and width are the height and width of the DOM element as it exists in the dashboard.
  // There is also a `data` prop, which is, of course, your DATA 🎉
  const {
    height,
    width,
    dataDumpRows,
    dataDumpAggregate,
    hourlyDataDumpAggregates,
    shiftName,
    isPartialDataset,
    teamSizeChangesHistory,
    skuCodeChangesHistory,
    hourlyTopDowntimes,
    totalDowntime,
    totalDowntimePercent,
    isCurrentShift,
  } = props;

  // how many different skus are in the rows
  const uniqueSkuCodes = dataDumpRows.reduce(
    (acc, cur) => {
      if (!cur.skuCode || cur.skuCode in acc) {
        return acc;
      }
      acc[cur.skuCode] = true;
      return acc;
    },
    {} as { [skuCode: string]: boolean }
  );

  const numSkus = Object.keys(uniqueSkuCodes).length;

  const [ref, dimensions] = useMeasure<HTMLDivElement>();
  const paddingWidth = dimensions.width * COLUMN_PADDING_HZ_PCT;
  const colWidths = COLUMN_WIDTHS_PCT.map(pct => pct * dimensions.width);

  // when we run a new query and get a new dataset, flicker the table in and out,
  // otherwise the <table> element's height stays at least as tall as the last time we ran the query
  const [flicker, setFlicker] = useState(true);
  useEffect(() => {
    setFlicker(false);
    setTimeout(() => {
      setFlicker(true);
    });
  }, [dataDumpRows]);

  const scale = Math.max(1, dimensions.width / BREAKPOINT_DESKTOP_XL);

  return flicker ? (
    <>
      <div style={{ width, maxWidth: "100vw" }} ref={ref}></div>
      <Styles
        $boldText={props.boldText}
        $headerFontSize={props.headerFontSize}
        $height={height}
        $width={width}
        $theme={defaultSupersetTheme}
        $scale={scale}
      >
        {isPartialDataset && (
          <div className="partial-dataset-indicator">{`⚠ time range is > 1week (displaying first week only)`}</div>
        )}
        <CardStyles style={{ marginTop: 8 }} $scale={scale}>
          <div className="card-header">{shiftName}</div>
          <ErrorBoundary>
            {hourlyDataDumpAggregates.length === 0 ? (
              <div style={{ padding: 96 }}>
                <Spinner />
              </div>
            ) : null}
            <VerticalTimeline
              {...{
                hourlyDataDumpAggregates,
                paddingWidth,
                colWidths,
                teamSizeChangesHistory,
                skuCodeChangesHistory,
                hourlyTopDowntimes,
                isCurrentShift,
                scale,
              }}
            />
          </ErrorBoundary>
          <ErrorBoundary>
            <TotalsRow
              {...{
                dataDumpAggregate,
                numSkus,
                paddingWidth,
                colWidths,
                totalDowntime,
                totalDowntimePercent,
                scale,
              }}
            />
          </ErrorBoundary>
        </CardStyles>
      </Styles>
    </>
  ) : null;
}

type MeritTier0StyledComponentProps = ChartPluginsStyledComponentProps & {
  $scale: number;
};
const Styles = styled.div<MeritTier0StyledComponentProps>`
  /* @font-face {
    font-family: "GothamBook";
    src: local("GothamBook"), url(../fonts/Gotham-Book.woff) format("woff");
  }
  @font-face {
    font-family: "GothamMedium";
    src: local("GothamMedium"), url(../fonts/Gotham-Medium.woff) format("woff");
  } */
  @font-face {
    font-family: "Open Sans";
    src:
      local("Open Sans"),
      url(../fonts/OpenSans.woff) format("woff");
  }
  /* @font-face {
    font-family: "Helvetica Neue Bold";
    src: local("Helvetica Neue Bold"), url(../fonts/HelveticaNeue-Bold.woff) format("woff");
  }
  @font-face {
    font-family: "Helvetica Neue";
    src: local("Helvetica Neue"), url(../fonts/HelveticaNeue-Medium.woff) format("woff");
  }
  @font-face {
    font-family: "Helvetica Neue Light";
    src: local("Helvetica Neue Light"), url(../fonts/HelveticaNeue-Light.woff) format("woff");
  }
  @font-face {
    font-family: "Helvetica Neue Thin";
    src: local("Helvetica Neue Thin"), url(../fonts/HelveticaNeue-Thin.woff) format("woff");
  } */
  @font-face {
    font-family: "Roboto";
    src:
      local("Roboto"),
      url(../fonts/Roboto.woff) format("woff");
  }
  box-sizing: border-box;
  background-color: ${lightSurface0};
  padding: ${({ $scale, $theme }: MeritTier0StyledComponentProps) =>
    $scale * $theme.gridUnit * 4}px;
  border-radius: ${({ $scale, $theme }: MeritTier0StyledComponentProps) =>
    $scale * $theme.gridUnit * 2}px;
  min-height: ${({ $height }: MeritTier0StyledComponentProps) => $height};
  width: ${({ $width }: MeritTier0StyledComponentProps) => $width};
  max-width: 98vw;
  flex-grow: 0;
  overflow-y: scroll;

  display: flex;
  flex-direction: column;

  h3 {
    /* You can use your props to control CSS! */
    font-size: ${({
      $scale,
      $theme,
      $headerFontSize,
    }: MeritTier0StyledComponentProps) =>
      $scale * $theme.typography.sizes[$headerFontSize]}px;
    font-weight: ${({ $theme, $boldText }: MeritTier0StyledComponentProps) =>
      $theme.typography.weights[$boldText ? "bold" : "normal"]};
  }
  .partial-dataset-indicator {
    font-family: "Open Sans";
    background: ${summarySectionBottomBackground};
    margin-bottom: ${p => p.$scale * 8}px;
    width: fit-content;
    padding: ${p => p.$scale * 8}px;
  }
`;

const CardStyles = styled.div<{ $scale: number }>`
  &,
  * {
    box-sizing: border-box;
  }
  flex-grow: 1;
  overflow: hidden;
  background: white;
  border-radius: ${p => p.$scale * 8}px;
  border: ${p => p.$scale * 1}px solid #eaeaea;
  .card-header {
    padding: ${p => p.$scale * 8}px 0 ${p => p.$scale * 4}px;
    line-height: ${p => p.$scale * 22}px;
    font-size: ${p => p.$scale * 14}px;
    text-align: center;
    font-family: "Open Sans", sans-serif;
    color: ${lightTextSubheading};
    font-weight: bold;
  }
`;
